import { useParams, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import useTheme from '@mui/styles/useTheme';
import Grid from '@mui/material/Grid';
import CssBaseline from '@mui/material/CssBaseline';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import React, { useState, useCallback, useEffect } from 'react';
import { CircularProgress } from '@mui/material';
import CommonBackdrop from '../../../components/CommonBackdrop';
import BackButton from '../../../components/BackButton';
import ProductAttributesSection from './ProductAttributesSection';
import ProductEmptyDetailsSection from './ProductEmptyDetailsSection';
import ProductHeaderSection from './ProductHeaderSection';
import ProductImageSection from './ProductImageSection';
import ProductOverviewSection from './ProductOverviewSection';
import ProductPOListSection from './ProductPOListSection';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
import PageContentContainer from '../../../components/PageContentContainer';
import Error from '../../../components/Error';
import { useProductsApi } from '../../../apis';
import { downloadFileFromApi } from '../../../apis/Utilities/downloadFileFromApi';

function ProductDetails({ namespace }) {
	const { t } = useTranslation(namespace, 'common');
	const theme = useTheme();
	const DEFAULT_DISPLAY_ROW_COUNT = 3;
	const isMountedRef = useIsMountedRef();
	const [product, setProduct] = useState({});
	const [isLoading, setIsLoading] = useState(false);
	const [isError, setIsError] = useState(false);
	const [printCardDownloadError, setPrintCardDownloadError] = useState('');
	const navigate = useNavigate();
	const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_DISPLAY_ROW_COUNT);

	const { productId } = useParams();
	const { downloadPrintCard, getDetails: getProductDetails } = useProductsApi();

	const showAllPO = () => {
		if (!product.POList) {
			return;
		}
		if (rowsPerPage === DEFAULT_DISPLAY_ROW_COUNT) {
			setRowsPerPage(product.POList.length);
		} else {
			setRowsPerPage(DEFAULT_DISPLAY_ROW_COUNT);
		}
	};

	const handlePoRowClick = (orderNumber, source) => {
		navigate(`/orders/${orderNumber}/${source}`);
	};

	const handlePrintCardDownload = async () => {
		setPrintCardDownloadError('');
		await downloadFileFromApi({
			format: 'pdf',
			defaultFilenamePrefix: `PrintCard_${productId}`,
			executeApiCall: () => downloadPrintCard(productId),
			onError: () => {
				setPrintCardDownloadError(t('common:downloadError'));
			},
		});
	};

	const fetchProduct = useCallback(async () => {
		setIsLoading(true);

		const response = await getProductDetails({ id: productId });

		if (response?.isAborted) {
			setIsLoading(false);
			return;
		}

		if (response?.isError) {
			console.log(response.error || 'failed to load product details');
			setIsError(true);
			setIsLoading(false);
			return;
		}

		if (isMountedRef.current) {
			const data = response?.data || {};
			setProduct(data);
			setIsError(false);
			window.dataLayer = window.dataLayer || [];
			window.dataLayer.push({
				event: 'view_item',
				item_id: data.Customer_Item_Number,
				item_name: data.Item_Description,
				location: data.Plant_Name,
			});
		}

		setIsLoading(false);
	}, [isMountedRef, productId, getProductDetails]);

	useEffect(() => {
		fetchProduct();
	}, [fetchProduct]);

	const getProductImage = () => {
		if (!product?.Product_Image) {
			return '';
		}

		return `data:image/png;base64,${product.Product_Image}`;
	};

	const hasAttributes = !!(
		product?.Attributes && product.Attributes.length > 0
	);

	const hasPOList = !!(product?.POList && product.POList.length > 0);

	const hasProductDetails =
		product?.Item_Description ||
		product?.Product_Image ||
		product?.Customer_Item_Number ||
		hasPOList ||
		hasAttributes ||
		product?.Design_Number ||
		product?.Plant_Name ||
		product?.Quantity_Ordered ||
		product?.Quantity_Shipped ||
		product?.PrintCard;

	const showNoDetailsBackdrop = isError || (!isError && !product && !isLoading);
	const contentTopSpacingStyle = { marginTop: theme.spacing(2.5) };

	return (
		<>
			<CssBaseline />
			<CommonBackdrop role="progressbar" open={isLoading}>
				<CircularProgress color="inherit" role="presentation" />
			</CommonBackdrop>

			{!isLoading && (
				<PageContentContainer>
					<Grid item xs={12} aria-label={t('back')}>
						<BackButton />
					</Grid>
					{!!printCardDownloadError && (
						<Grid item xs={12} style={{ marginTop: theme.spacing(2) }}>
							<Error message={printCardDownloadError} fullWidth />
						</Grid>
					)}
					{showNoDetailsBackdrop ? (
						<Grid
							item
							xs={12}
							aria-label={t('no_details_found')}
							style={contentTopSpacingStyle}
						>
							<Typography variant="body1" role="paragraph">
								{t('no_details_found')}
							</Typography>
						</Grid>
					) : (
						<Grid
							item
							xs={12}
							aria-label={t('product_details')}
							style={contentTopSpacingStyle}
						>
							<Grid container spacing={2}>
								<ProductHeaderSection product={product} theme={theme} t={t} />
								<ProductImageSection
									theme={theme}
									imageSrc={getProductImage()}
									hasDescriptionAndDesign={
										!!(product.Item_Description && product.Design_Number)
									}
								/>
							</Grid>

							{!hasProductDetails ? (
								<ProductEmptyDetailsSection theme={theme} t={t} />
							) : (
								<Grid container style={{ marginTop: theme.spacing(4) }}>
									<ProductOverviewSection
										product={product}
										theme={theme}
										t={t}
										onPrintCardDownload={handlePrintCardDownload}
									/>

									<ProductAttributesSection
										attributes={product.Attributes || []}
										theme={theme}
										t={t}
									/>

									<ProductPOListSection
										poList={product.POList || []}
										source={product.Source}
										theme={theme}
										t={t}
										rowsPerPage={rowsPerPage}
										defaultDisplayRowCount={DEFAULT_DISPLAY_ROW_COUNT}
										onToggleRows={showAllPO}
										onRowClick={handlePoRowClick}
									/>
								</Grid>
							)}
						</Grid>
					)}
				</PageContentContainer>
			)}
		</>
	);
}

ProductDetails.propTypes = {
	namespace: PropTypes.string.isRequired,
};

export default ProductDetails;
